import { useTranslation } from "react-i18next";
import { STATUS_IN_PROGRESS } from "../../hooks/useMaintenanceBanner";
import { parseBackendDate } from "utils";

// The wording of a maintenance window, shared by the banner and the dashboard
// Messages section so the two always say the same thing.
const useMaintenanceText = (window) => {
  const { t, i18n } = useTranslation("common");

  const isInProgress = window.status === STATUS_IN_PROGRESS;
  const tk = window.translationKey;

  // Looks up banner.{suffix}.{translationKey} first, falls back to banner.{suffix}
  const bt = (suffix, params) =>
    t(`maintenanceBanner.${suffix}.${tk}`, {
      ...params,
      defaultValue: t(`maintenanceBanner.${suffix}`, params),
    });

  const title = isInProgress ? bt("titleActive") : bt("titlePlanned");

  const formatDate = (iso) => {
    if (!iso) return null;
    const date = parseBackendDate(iso);
    if (date === null) return null;
    const formatted = date.toLocaleString(i18n.language, {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
    const offsetMinutes = -date.getTimezoneOffset();
    const sign = offsetMinutes >= 0 ? "+" : "-";
    const absHours = String(Math.floor(Math.abs(offsetMinutes) / 60)).padStart(2, "0");
    const absMins = String(Math.abs(offsetMinutes) % 60).padStart(2, "0");
    return `${formatted} (UTC${sign}${absHours}:${absMins})`;
  };

  const formatDuration = (startIso, endIso) => {
    if (!startIso || !endIso) return null;
    const diffMs = new Date(endIso) - new Date(startIso);
    if (diffMs <= 0) return null;
    const totalMinutes = Math.round(diffMs / 60000);
    if (totalMinutes === 0) return null;
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    if (hours === 0) return `${minutes}m`;
    return minutes === 0 ? `${hours}h` : `${hours}h ${minutes}m`;
  };

  const startDate = formatDate(window.startDateUtc);
  const duration = formatDuration(window.startDateUtc, window.plannedEndDateUtc);

  const scheduleText = isInProgress
    ? bt("inProgressSince", { date: startDate })
    : bt("scheduledOn", { date: startDate });

  const durationText = duration ? bt("duration", { duration }) : null;

  const impactText = window.possibleDataLoss
    ? bt("impactWithDataLoss")
    : bt("impactNoDataLoss");

  return { isInProgress, title, scheduleText, durationText, impactText };
};

export default useMaintenanceText;
